// src/components/tasks/TaskPickerModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Package,
  Search,
  Filter,
  Loader2,
  ArrowLeft,
  Layers,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import ProductionTaskCard from './ProductionTaskCard';
import ProductionTaskDetailModal from './ProductionTaskDetailModal';
import { getProductionTaskById } from '../../api/productionTask.api';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: '1', label: 'Pending' },
  { value: '2', label: 'In Process' },
  { value: '3', label: 'Completed' },
];

/**
 * TaskPickerModal — two-step modal for the Time Entries page.
 *
 * Step 1  → grid of the user's production tasks (compact cards).
 * Step 2  → full ProductionTaskDetailModal for the selected task.
 */
const TaskPickerModal = ({
  isOpen,
  onClose,
  tasks = [],
  onSelectTask,
  onStartTimer,
  runningEntries = [],
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  onPageChange,
  paginationLoading = false,
  initialTaskId = null,
  initialOpenKey = 0,
  searchTerm = '',
  filterStatus = '',
  onSearchTermChange,
  onFilterStatusChange,
}) => {
  // ── internal state ──
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  // ── server-side filtering: use tasks as-is from parent ──
  const filteredTasks = tasks;

  // ── card click → fetch full details & open detail modal ──
  const handleCardClick = useCallback(async (task) => {
    setDetailLoading(true);
    setShowDetail(true);
    setSelectedTask(null);

    try {
      const lotId = task.productionLotId || task.id;
      if (lotId) {
        const response = await getProductionTaskById(lotId);
        const detailedData = response?.data ?? response;
        setSelectedTask(detailedData);
      } else {
        setSelectedTask(task);
      }
    } catch {
      // fallback to card-level data
      setSelectedTask(task);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !initialTaskId) return;

    let cancelled = false;

    const openInitialTask = async () => {
      setDetailLoading(true);
      setShowDetail(true);
      setSelectedTask(null);

      try {
        const response = await getProductionTaskById(initialTaskId);
        const detailedData = response?.data ?? response;
        if (!cancelled) {
          setSelectedTask(detailedData);
        }
      } catch {
        if (!cancelled) {
          setSelectedTask({
            id: initialTaskId,
            productionLotId: initialTaskId,
            title: `Production Lot ${initialTaskId}`,
          });
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    };

    openInitialTask();

    return () => {
      cancelled = true;
    };
  }, [isOpen, initialTaskId, initialOpenKey]);

  // ── back from detail to card grid ──
  const handleBackToList = useCallback(() => {
    setShowDetail(false);
    setSelectedTask(null);
  }, []);

  // ── close everything ──
  const handleClose = useCallback(() => {
    setShowDetail(false);
    setSelectedTask(null);
    if (onSearchTermChange) onSearchTermChange('');
    if (onFilterStatusChange) onFilterStatusChange('');
    onClose();
  }, [onClose, onSearchTermChange, onFilterStatusChange]);

  if (!isOpen) return null;

  // ── Step 2: detail view (reuse existing modal) ──
  if (showDetail) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
        {/* backdrop */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

        <div className="relative z-10 w-full max-w-5xl max-h-[90vh] flex flex-col">
          {/* Back button floating above modal */}
          <button
            onClick={handleBackToList}
            className="mb-2 self-start flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur rounded-xl text-gray-700 hover:bg-white hover:text-sap-blue transition-all shadow-lg text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tasks
          </button>

          <ProductionTaskDetailModal
            isOpen={true}
            onClose={handleBackToList}
            task={selectedTask}
            loading={detailLoading}
            mode="view"
            runningEntries={runningEntries}
            onStartTimer={onStartTimer ? (task, accumulatedSeconds, parentTimeEntryId, activity) => {
              onStartTimer(task, accumulatedSeconds, parentTimeEntryId, activity);
              handleClose();
            } : undefined}
          />
        </div>
      </div>,
      document.body
    );
  }

  // ── Step 1: card grid ──
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative z-10 bg-white dark:bg-gray-900 rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700">
        {/* ── Header ── */}
        <div className="px-3 py-3 sm:px-6 sm:py-5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-slate-50 to-blue-50 dark:from-gray-800 dark:to-gray-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl shadow-md">
              <Layers className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white">
                My Production Tasks
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {totalCount} task{totalCount !== 1 ? 's' : ''} available &mdash; click to view details
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* ── Filters: search bar + status dropdown ── */}
        <div className="px-3 py-2 sm:px-6 sm:py-3 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Lot ID, Order ID, Product..."
                value={searchTerm}
                onChange={(e) => onSearchTermChange?.(e.target.value)}
                className="w-full pl-10 pr-4 py-1.5 sm:py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg sm:rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent dark:bg-gray-800 dark:text-gray-100 placeholder-gray-400"
              />
            </div>
            <div className="relative w-full sm:w-44">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => onFilterStatusChange?.(e.target.value)}
                className="w-full pl-10 pr-8 py-1.5 sm:py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg sm:rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent dark:bg-gray-800 dark:text-gray-100 cursor-pointer appearance-none"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* ── Card grid ── */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          {paginationLoading ? (
            <div className="flex flex-col items-center justify-center py-10 sm:py-16 text-gray-400 dark:text-gray-500">
              <Loader2 className="w-8 h-8 sm:w-10 sm:h-10 animate-spin mb-3 text-blue-500" />
              <p className="text-xs sm:text-sm font-medium">Loading tasks...</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 sm:py-16 text-gray-400 dark:text-gray-500">
              <Package className="w-10 h-10 sm:w-12 sm:h-12 mb-3 opacity-40" />
              <p className="text-xs sm:text-sm font-medium">
                {paginationLoading ? 'Loading tasks...' : searchTerm || filterStatus ? 'No tasks match your search' : 'No production tasks available'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
              {filteredTasks.map((task) => {
                const taskIsRunning = runningEntries.some(
                  e => e.referenceProductionTaskId === (task.productionLotId || task.id) ||
                       e.productionTaskId === (task.productionLotId || task.id)
                );
                return (
                  <ProductionTaskCard
                    key={task.id || task.productionLotId}
                    task={task}
                    onClick={() => handleCardClick(task)}
                    isRunning={taskIsRunning}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* ── Footer with pagination ── */}
        <div className="px-3 py-2 sm:px-6 sm:py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-between text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
          <span>
            Showing {filteredTasks.length} of {totalCount} tasks
            {totalPages > 1 && ` — Page ${currentPage} of ${totalPages}`}
          </span>
          {totalPages > 1 && onPageChange && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 1 || paginationLoading}
                className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {(() => {
                const pages = [];
                let start = Math.max(1, currentPage - 2);
                let end = Math.min(totalPages, start + 4);
                if (end - start < 4) start = Math.max(1, end - 4);
                for (let i = start; i <= end; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => onPageChange(i)}
                      disabled={paginationLoading}
                      className={`min-w-[28px] h-7 rounded-lg text-xs font-semibold transition-all ${
                        i === currentPage
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                      } disabled:opacity-50`}
                    >
                      {i}
                    </button>
                  );
                }
                return pages;
              })()}
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages || paginationLoading}
                className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          <button
            onClick={handleClose}
            className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default React.memo(TaskPickerModal);
